import React from "react";

import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { Modal } from "../../../common/Modal";
import { AlertIcon } from "../../../../assets/ui/icons";
import { EditDialog } from "./EditDialog";
import { FileDropzone } from "./FileDropzone";
import { SectionNotice } from "./SectionNotice";

/* Method 2 — a dropzone per year, and the year rules on submit.
 *
 * The design lays the six years out two to a row, each with the shared
 * dropzone, under a notice explaining that a person reads these.
 */
export const FinancialDocumentsPanel = ({ uploads, className = "" }) => {
  const {
    years,
    documents,
    attach,
    remove,
    uploadingYear,
    error,
    missing,
    warningOpen,
    closeWarning,
    acceptWarning,
    busy,
  } = uploads;

  return (
    <div className={className}>
      <SectionNotice tone="info">
        You are requesting TREVEX to fill your details. Make sure to upload the
        correct document. This may take 2–3 working days.
      </SectionNotice>

      {error && (
        <p
          role="alert"
          className="m-0 mt-3 text-[12.5px] font-semibold text-[var(--coral-deep)] font-sans"
        >
          {error}
        </p>
      )}

      <div
        className={cn(
          "mt-4 rounded-[14px] border border-[var(--line)] bg-white",
          "p-4 sm:p-6"
        )}
      >
        <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
          {years.map((year) => {
            const url = documents[year];
            return (
              <div key={year}>
                <p
                  className={cn(
                    "m-0 mb-2 text-[13px] font-bold font-sans",
                    /* A year the rules require but that has nothing yet. */
                    missing.includes(year)
                      ? "text-[var(--coral-deep)]"
                      : "text-[var(--ink)]"
                  )}
                >
                  Year {year}
                  {missing.includes(year) && (
                    <span className="ml-1.5 font-semibold">· required</span>
                  )}
                </p>

                {uploadingYear === year ? (
                  <p
                    role="status"
                    aria-live="polite"
                    className="m-0 text-[13px] text-[var(--muted)] font-sans"
                  >
                    Uploading…
                  </p>
                ) : url ? (
                  <div
                    className={cn(
                      "flex flex-wrap items-center justify-between gap-3 rounded-[12px]",
                      "border border-[var(--line)] bg-[var(--field-inset)] px-4 py-3"
                    )}
                  >
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[var(--coral)] font-sans"
                    >
                      {`Statement for ${year}`}
                    </a>
                    <button
                      type="button"
                      onClick={() => remove(year)}
                      aria-label={`Remove the statement for ${year}`}
                      className={cn(
                        "rounded px-1 text-[12px] font-bold text-[var(--muted)] font-sans",
                        "transition-colors hover:text-[var(--coral-deep)]",
                        "focus-visible:outline-none focus-visible:ring-2",
                        "focus-visible:ring-[var(--coral)]"
                      )}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <FileDropzone
                    label={`Statement for ${year}`}
                    files={[]}
                    onChange={(next) => attach(year, next?.[0])}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Transcribed: accepting this DISCARDS the rows it names, so it is a
          confirmation rather than a notice. */}
      {warningOpen && (
        <Modal modalHandle={closeWarning} noClose bare>
          <EditDialog title="Older statements will be removed" onClose={closeWarning} width={460}>
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-[2px] shrink-0 text-[var(--warning)]"
              >
                <AlertIcon size={18} />
              </span>
              <p className="m-0 text-[13px] leading-[1.55] text-[var(--ink-2)] font-sans">
                We accept financial data for the last 5 years only. Continuing
                will remove the statements you have added for earlier years.
              </p>
            </div>

            <div className="mt-4 flex flex-wrap justify-end gap-3">
              <Button variant="outline" onClick={closeWarning}>
                Go back
              </Button>
              <Button variant="coralSolid" disabled={busy} onClick={acceptWarning}>
                {busy ? "Uploading…" : "Continue anyway"}
              </Button>
            </div>
          </EditDialog>
        </Modal>
      )}
    </div>
  );
};

export default FinancialDocumentsPanel;
